import { useState, useRef } from "react";
import { useExtractFileData } from "@/hooks/useExtractFileData";
import type { DocumentType } from "@/lib/file-extraction";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const DOCUMENT_TYPES: { value: DocumentType; label: string }[] = [
  { value: "notification", label: "Notification (xlsx)" },
  { value: "impact", label: "Impact Analysis (xlsx)" },
  { value: "notification_decision", label: "Notification Decision (pdf)" },
  { value: "risk_analysis", label: "Risk Analysis (xlsx)" },
];

export default function FileExtractionTest() {
  const [documentType, setDocumentType] =
    useState<DocumentType>("notification");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const extraction = useExtractFileData();

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setSelectedFiles(Array.from(files));
    }
  };

  const handleExtract = () => {
    if (selectedFiles.length === 0) return;
    extraction.mutate({ files: selectedFiles, documentType });
  };

  const handleClear = () => {
    setSelectedFiles([]);
    extraction.reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">File Extraction Test</h1>
          <Badge variant="outline" className="text-xs">
            DEBUG PAGE
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload & Extract</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {DOCUMENT_TYPES.map((dt) => (
                <Button
                  key={dt.value}
                  variant={documentType === dt.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDocumentType(dt.value)}
                >
                  {dt.label}
                </Button>
              ))}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".xlsx,.xls,.pdf"
              onChange={handleFilesChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:rounded file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
            />

            {selectedFiles.length > 0 && (
              <div className="text-sm text-gray-600">
                {selectedFiles.map((f) => f.name).join(", ")}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleExtract}
                disabled={selectedFiles.length === 0 || extraction.isPending}
              >
                {extraction.isPending ? "Extracting..." : "Extract Data"}
              </Button>
              <Button variant="outline" onClick={handleClear}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {extraction.error && (
          <Card className="border-red-300 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-700">Unhandled Error</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm text-red-800">
                {extraction.error.message}
              </pre>
            </CardContent>
          </Card>
        )}

        {extraction.data && (
          <>
            <StatusCard data={extraction.data} />
            <ErrorsCard
              title="Errors"
              items={extraction.data.errors}
              variant="error"
            />
            <ErrorsCard
              title="Warnings"
              items={extraction.data.warnings}
              variant="warning"
            />
            <JsonCard title="Merged Data" data={extraction.data.mergedData} />
            {extraction.data.fileResults.map((result, i) => (
              <Card key={i}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span>{result.fileName}</span>
                    <Badge variant={result.isValid ? "default" : "destructive"}>
                      {result.isValid ? "Valid" : "Invalid"}
                    </Badge>
                    {result.version && (
                      <Badge variant="outline">{result.version}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CollapsibleJson
                    title="Extracted Fields"
                    data={result.extractedData?.fields}
                  />
                  <CollapsibleJson
                    title="Transformed Data"
                    data={result.transformedData}
                  />
                  <CollapsibleJson title="Full Result" data={result} />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function StatusCard({
  data,
}: {
  data: {
    isValid: boolean;
    errors: unknown[];
    warnings: unknown[];
    fileResults: unknown[];
  };
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 pt-6">
        <Badge
          variant={data.isValid ? "default" : "destructive"}
          className="text-sm"
        >
          {data.isValid ? "VALID" : "INVALID"}
        </Badge>
        <span className="text-sm text-gray-600">
          {data.fileResults.length} file(s) processed
        </span>
        <span className="text-sm text-gray-600">
          {data.errors.length} error(s)
        </span>
        <span className="text-sm text-gray-600">
          {data.warnings.length} warning(s)
        </span>
      </CardContent>
    </Card>
  );
}

function ErrorsCard({
  title,
  items,
  variant,
}: {
  title: string;
  items: { file: string; message: string; code: string; field?: string }[];
  variant: "error" | "warning";
}) {
  if (items.length === 0) return null;

  const bgColor =
    variant === "error"
      ? "bg-red-50 border-red-200"
      : "bg-yellow-50 border-yellow-200";
  const textColor = variant === "error" ? "text-red-800" : "text-yellow-800";

  return (
    <Card className={bgColor}>
      <CardHeader>
        <CardTitle className={`text-base ${textColor}`}>
          {title} ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1">
          {items.map((item, i) => (
            <li key={i} className={`text-sm ${textColor}`}>
              <span className="font-mono font-semibold">[{item.code}]</span>{" "}
              {item.field && (
                <span className="font-mono text-xs">({item.field})</span>
              )}{" "}
              {item.message}
              <span className="text-xs opacity-60"> — {item.file}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function JsonCard({ title, data }: { title: string; data: unknown }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <pre className="max-h-[600px] overflow-auto rounded bg-gray-900 p-4 text-xs text-green-400">
          {JSON.stringify(data, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}

function CollapsibleJson({ title, data }: { title: string; data: unknown }) {
  const [open, setOpen] = useState(false);

  if (!data) return null;

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="text-sm font-medium text-blue-600 hover:underline"
      >
        {open ? "▼" : "▶"} {title}
      </button>
      {open && (
        <pre className="mt-2 max-h-[400px] overflow-auto rounded bg-gray-900 p-3 text-xs text-green-400">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
